import { FileQuestion } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import {
  StatePlaceholder,
  type StatePlaceholderIconProps,
  type StatePlaceholderProps,
} from '../state-placeholder';

import { notFoundPlaceholderTestId } from './not-found-placeholder-test-ids';

export type NotFoundPlaceholderProps = Omit<
  StatePlaceholderProps,
  'role' | 'testId'
>;

const NotFoundIcon = ({
  children,
  className,
  tileClassName,
}: StatePlaceholderIconProps) => (
  <StatePlaceholder.Icon
    className={cn('text-muted-foreground', className)}
    tileClassName={cn('bg-card', tileClassName)}
  >
    {children ?? <FileQuestion />}
  </StatePlaceholder.Icon>
);

/**
 * The shared "this does not exist" state: icon, title, description, optional action.
 *
 * Announces as `status`, not `alert`. A 404 is an authoritative answer about a URL, not a
 * failure that should interrupt the reader — the same argument the `restricted` sibling makes.
 *
 * Presentation-only: pass router links or buttons in from the app layer, and localized copy
 * with them, because `libs/ui` carries no i18n.
 *
 * @example
 * <NotFoundPlaceholder density="page">
 *   <NotFoundPlaceholder.Icon />
 *   <NotFoundPlaceholder.Content>
 *     <NotFoundPlaceholder.Title>{t('notFound.title')}</NotFoundPlaceholder.Title>
 *     <NotFoundPlaceholder.Description>{t('notFound.description')}</NotFoundPlaceholder.Description>
 *   </NotFoundPlaceholder.Content>
 *   <NotFoundPlaceholder.Action><Button asChild><Link to="/">Home</Link></Button></NotFoundPlaceholder.Action>
 * </NotFoundPlaceholder>
 */
const NotFoundPlaceholderRoot = React.forwardRef<
  HTMLDivElement,
  NotFoundPlaceholderProps
>(({ children, ...rest }, ref) => (
  <StatePlaceholder
    ref={ref}
    {...rest}
    role="status"
    testId={notFoundPlaceholderTestId}
  >
    {children}
  </StatePlaceholder>
));

NotFoundPlaceholderRoot.displayName = 'NotFoundPlaceholder';
NotFoundIcon.displayName = 'NotFoundPlaceholder.Icon';

export const NotFoundPlaceholder = Object.assign(NotFoundPlaceholderRoot, {
  Icon: NotFoundIcon,
  Content: StatePlaceholder.Content,
  Title: StatePlaceholder.Title,
  Description: StatePlaceholder.Description,
  Action: StatePlaceholder.Action,
});
